import { useGet } from './useFetch'
import { useAuth } from './useAuth'
import { ApiResponse } from '@magnetar/admin-api-interfaces'

/**
 * Read-only supplier view: full template structure with slots, but only
 * containing this supplier's items, and with BE-computed `expected_play_seconds`
 * on each item.
 *
 * Matches `GET /api/ad-supplier/:supplier_id/ad-schedule-templates/:template_id`.
 *
 * `expected_play_seconds` = `slot.play_minute * 60` + cumulative `play_duration`
 * of all earlier items in the same slot (across ALL suppliers). The BE computes
 * the offset against the full unfiltered slot, then strips down to this
 * supplier's items, so positions match what actually plays.
 */
export interface SupplierAdScheduleSlotItem {
  item_id: number
  slot_id: number
  advertisement_id: number
  order: number
  play_duration: number
  expected_play_seconds: number
  created_at: string
  updated_at: string
  is_active: boolean
  advertisement: {
    advertisement_id: number
    supplier_id: number
    title?: string | null
    description?: string | null
    created_at: string
    is_active: boolean
  }
}

export interface SupplierAdScheduleSlot {
  slot_id: number
  template_id: number
  slot_number: number
  slot_name?: string | null
  total_duration: number
  play_minute: number
  created_at: string
  updated_at: string
  is_active: boolean
  slot_items: SupplierAdScheduleSlotItem[]
}

export interface SupplierAdScheduleTemplate {
  template_id: number
  template_name: string
  description?: string | null
  provider_id?: number | null
  is_active: boolean
  duration_limit_enabled: boolean
  created_at: string
  updated_at: string
  slots: SupplierAdScheduleSlot[]
}

interface UseSupplierAdScheduleParams {
  supplierId: number | null | undefined
  templateId: number | null | undefined
}

export function useSupplierAdSchedule({
  supplierId,
  templateId,
}: UseSupplierAdScheduleParams) {
  const { token } = useAuth()

  const url =
    token && supplierId && templateId
      ? `/api/ad-supplier/${supplierId}/ad-schedule-templates/${templateId}`
      : null

  const { data, error, mutate } = useGet<ApiResponse<SupplierAdScheduleTemplate>>(
    url,
    undefined,
    token ?? '',
  )

  return {
    template: data?.data ?? null,
    isLoading: !!url && !error && !data,
    isError: error,
    mutate,
  }
}

/**
 * Format `expected_play_seconds` as "X分YY秒" — e.g. 185s → "3分05秒".
 */
export function formatExpectedPlayTime(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds))
  const m = Math.floor(safe / 60)
  const s = safe % 60
  return `${m}分${String(s).padStart(2, '0')}秒`
}
